import type { StaticModelConfigurationType } from "./types";

// Bedrock-provider models registered 2026-09-07. Every entry was
// live-verified in ap-southeast-2 via a Bedrock Converse completion before
// registration, and priced from the AWS Price List bulk API (AmazonBedrock
// offer, ap-southeast-2 on-demand standard tier, 2026-09-01 offer).
// Specs come from the per-model AWS Bedrock card (docs.aws.amazon.com under
// bedrock/latest/userguide/model-card-*).

export const NOVAPRO_MODEL_ID = "nova-pro" as const;
export const NOVAPRO_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NOVAPRO_MODEL_ID,
  modelMaker: "amazon",
  displayName: "Nova Pro",
  contextSize: 300_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Amazon's most capable Nova model for complex multimodal tasks (300k context).",
  shortDescription: "Amazon's frontier Nova model.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 4_096,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NOVALITE_MODEL_ID = "nova-lite" as const;
export const NOVALITE_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NOVALITE_MODEL_ID,
  modelMaker: "amazon",
  displayName: "Nova Lite",
  contextSize: 300_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Amazon's fast, low-cost multimodal Nova model (300k context).",
  shortDescription: "Amazon's fast, affordable Nova.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 4_096,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NOVAMICRO_MODEL_ID = "nova-micro" as const;
export const NOVAMICRO_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NOVAMICRO_MODEL_ID,
  modelMaker: "amazon",
  displayName: "Nova Micro",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Amazon's ultra-light text-only Nova model (128k context).",
  shortDescription: "Amazon's ultra-light text model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 4_096,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NOVA2LITE_MODEL_ID = "nova-2-lite" as const;
export const NOVA2LITE_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NOVA2LITE_MODEL_ID,
  modelMaker: "amazon",
  displayName: "Nova 2 Lite",
  contextSize: 1_000_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Amazon's Nova 2 Lite with 1M context and strong agentic performance.",
  shortDescription: "Amazon's next-gen Lite model.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 8_192,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GEMMA34B_MODEL_ID = "gemma-3-4b" as const;
export const GEMMA34B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GEMMA34B_MODEL_ID,
  modelMaker: "google",
  displayName: "Gemma 3 4B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Google's compact Gemma 3 model (128k context, image input).",
  shortDescription: "Google's compact open model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GEMMA312B_MODEL_ID = "gemma-3-12b" as const;
export const GEMMA312B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GEMMA312B_MODEL_ID,
  modelMaker: "google",
  displayName: "Gemma 3 12B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Google's mid-size Gemma 3 model (128k context, image input).",
  shortDescription: "Google's mid-size open model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GEMMA327B_MODEL_ID = "gemma-3-27b" as const;
export const GEMMA327B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GEMMA327B_MODEL_ID,
  modelMaker: "google",
  displayName: "Gemma 3 27B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Google's largest Gemma 3 model (128k context, image input).",
  shortDescription: "Google's largest open Gemma.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MINIMAXM2_MODEL_ID = "minimax-m2" as const;
export const MINIMAXM2_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MINIMAXM2_MODEL_ID,
  modelMaker: "minimax",
  displayName: "MiniMax M2",
  contextSize: 1_000_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "MiniMax's agentic reasoning model with 1M context.",
  shortDescription: "MiniMax's agentic model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MINIMAXM2P1_MODEL_ID = "minimax-m2p1" as const;
export const MINIMAXM2P1_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MINIMAXM2P1_MODEL_ID,
  modelMaker: "minimax",
  displayName: "MiniMax M2.1",
  contextSize: 196_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "MiniMax M2.1 with improved reasoning over M2 (196k context).",
  shortDescription: "MiniMax's improved M2 model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MINIMAXM2P5_MODEL_ID = "minimax-m2p5" as const;
export const MINIMAXM2P5_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MINIMAXM2P5_MODEL_ID,
  modelMaker: "minimax",
  displayName: "MiniMax M2.5",
  contextSize: 196_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "MiniMax M2.5, trained to reason efficiently (196k context).",
  shortDescription: "MiniMax's efficient reasoner.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const DEVSTRAL2123B_MODEL_ID = "devstral-2-123b" as const;
export const DEVSTRAL2123B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: DEVSTRAL2123B_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Devstral 2 123B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Mistral's coding model for agentic software engineering (256k context).",
  shortDescription: "Mistral's agentic coding model.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MAGISTRALSMALL2509_MODEL_ID = "magistral-small-2509" as const;
export const MAGISTRALSMALL2509_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MAGISTRALSMALL2509_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Magistral Small",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Mistral's reasoning model with image input (128k context).",
  shortDescription: "Mistral's reasoning model.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 16_384,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MINISTRAL33B_MODEL_ID = "ministral-3-3b" as const;
export const MINISTRAL33B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MINISTRAL33B_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Ministral 3 3B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Mistral's compact Ministral 3 model (128k context, image input).",
  shortDescription: "Mistral's compact 3B model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MINISTRAL38B_MODEL_ID = "ministral-3-8b" as const;
export const MINISTRAL38B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MINISTRAL38B_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Ministral 3 8B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Mistral's compact Ministral 3 model (128k context, image input).",
  shortDescription: "Mistral's compact 8B model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MINISTRAL314B_MODEL_ID = "ministral-3-14b" as const;
export const MINISTRAL314B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MINISTRAL314B_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Ministral 3 14B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Mistral's largest Ministral 3 model (128k context, image input).",
  shortDescription: "Mistral's largest Ministral 3.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MISTRAL7BINSTRUCT_MODEL_ID = "mistral-7b-instruct" as const;
export const MISTRAL7BINSTRUCT_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MISTRAL7BINSTRUCT_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Mistral 7B Instruct",
  contextSize: 32_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Mistral's original 7B instruct model (32k context).",
  shortDescription: "Mistral's original 7B model.",
  isLegacy: true,
  isLatest: false,
  generationTokensCount: 4_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MISTRALLARGE2402_MODEL_ID = "mistral-large-2402" as const;
export const MISTRALLARGE2402_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MISTRALLARGE2402_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Mistral Large 2 (2402)",
  contextSize: 32_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Mistral Large 2, the previous Mistral flagship (32k context).",
  shortDescription: "Mistral's previous flagship.",
  isLegacy: true,
  isLatest: false,
  generationTokensCount: 4_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const MIXTRAL8X7B_MODEL_ID = "mixtral-8x7b" as const;
export const MIXTRAL8X7B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: MIXTRAL8X7B_MODEL_ID,
  modelMaker: "mistral",
  displayName: "Mixtral 8x7B Instruct",
  contextSize: 32_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Mistral's Mixtral 8x7B sparse mixture-of-experts model (32k context).",
  shortDescription: "Mistral's sparse MoE model.",
  isLegacy: true,
  isLatest: false,
  generationTokensCount: 4_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const KIMIK2THINKING_MODEL_ID = "kimi-k2-thinking" as const;
export const KIMIK2THINKING_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: KIMIK2THINKING_MODEL_ID,
  modelMaker: "moonshot",
  displayName: "Kimi K2 Thinking",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Moonshot AI's Kimi K2 Thinking model for long-horizon reasoning (256k context).",
  shortDescription: "Moonshot's thinking model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const KIMIK2P5_MODEL_ID = "kimi-k2p5" as const;
export const KIMIK2P5_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: KIMIK2P5_MODEL_ID,
  modelMaker: "moonshot",
  displayName: "Kimi K2.5",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Moonshot AI's Kimi K2.5 multimodal model (256k context).",
  shortDescription: "Moonshot's multimodal K2.5.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 16_384,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NEMOTRONNANO12BV2_MODEL_ID = "nemotron-nano-12b-v2" as const;
export const NEMOTRONNANO12BV2_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NEMOTRONNANO12BV2_MODEL_ID,
  modelMaker: "nvidia",
  displayName: "Nemotron Nano 12B v2",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "NVIDIA's compact Nemotron Nano vision-language model (128k context).",
  shortDescription: "NVIDIA's compact VL model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NEMOTRONNANO330B_MODEL_ID = "nemotron-nano-3-30b" as const;
export const NEMOTRONNANO330B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NEMOTRONNANO330B_MODEL_ID,
  modelMaker: "nvidia",
  displayName: "Nemotron Nano 3 30B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "NVIDIA's efficient Nemotron Nano 3 model (256k context).",
  shortDescription: "NVIDIA's efficient Nano 3.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NEMOTRONNANO9BV2_MODEL_ID = "nemotron-nano-9b-v2" as const;
export const NEMOTRONNANO9BV2_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NEMOTRONNANO9BV2_MODEL_ID,
  modelMaker: "nvidia",
  displayName: "Nemotron Nano 9B v2",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "NVIDIA's compact Nemotron Nano model (128k context).",
  shortDescription: "NVIDIA's compact Nano model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const NEMOTRONSUPER3120B_MODEL_ID = "nemotron-super-3-120b" as const;
export const NEMOTRONSUPER3120B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: NEMOTRONSUPER3120B_MODEL_ID,
  modelMaker: "nvidia",
  displayName: "Nemotron Super 3 120B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "NVIDIA's Nemotron Super 3 model for agentic workloads (256k context).",
  shortDescription: "NVIDIA's Super 3 flagship.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GPTOSS120B_MODEL_ID = "gpt-oss-120b" as const;
export const GPTOSS120B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GPTOSS120B_MODEL_ID,
  modelMaker: "openai",
  displayName: "GPT-OSS 120B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "OpenAI's largest open-weight model with always-on reasoning (128k context).",
  shortDescription: "OpenAI's largest open-weight model.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GPTOSS20B_MODEL_ID = "gpt-oss-20b" as const;
export const GPTOSS20B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GPTOSS20B_MODEL_ID,
  modelMaker: "openai",
  displayName: "GPT-OSS 20B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "OpenAI's compact open-weight model with always-on reasoning (128k context).",
  shortDescription: "OpenAI's compact open-weight model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN3235BA22B_MODEL_ID = "qwen3-235b-a22b" as const;
export const QWEN3235BA22B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN3235BA22B_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 235B A22B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Alibaba's largest Qwen3 model (256k context).",
  shortDescription: "Qwen's largest Qwen3 model.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN332B_MODEL_ID = "qwen3-32b" as const;
export const QWEN332B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN332B_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 32B",
  contextSize: 32_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Alibaba's compact Qwen3 model (32k context).",
  shortDescription: "Qwen's compact Qwen3 model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN3CODER30B_MODEL_ID = "qwen3-coder-30b" as const;
export const QWEN3CODER30B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN3CODER30B_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 Coder 30B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Alibaba's compact Qwen3 coding model (256k context).",
  shortDescription: "Qwen's compact coding model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN3CODER480B_MODEL_ID = "qwen3-coder-480b" as const;
export const QWEN3CODER480B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN3CODER480B_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 Coder 480B",
  contextSize: 128_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Alibaba's largest Qwen3 coding model (128k context).",
  shortDescription: "Qwen's largest coding model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN3CODERNEXT_MODEL_ID = "qwen3-coder-next" as const;
export const QWEN3CODERNEXT_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN3CODERNEXT_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 Coder Next",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Alibaba's newest Qwen3 coding model (256k context).",
  shortDescription: "Qwen's newest coding model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN3NEXT80B_MODEL_ID = "qwen3-next-80b" as const;
export const QWEN3NEXT80B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN3NEXT80B_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 Next 80B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Alibaba's efficient Qwen3 Next model (256k context).",
  shortDescription: "Qwen's efficient Qwen3 Next.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 8_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const QWEN3VL235B_MODEL_ID = "qwen3-vl-235b" as const;
export const QWEN3VL235B_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: QWEN3VL235B_MODEL_ID,
  modelMaker: "qwen",
  displayName: "Qwen3 VL 235B",
  contextSize: 256_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Alibaba's Qwen3 vision-language model for visual reasoning (256k context).",
  shortDescription: "Qwen's vision-language flagship.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 8_000,
  supportsVision: true,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GLM47_MODEL_ID = "glm-4-7" as const;
export const GLM47_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GLM47_MODEL_ID,
  modelMaker: "zai",
  displayName: "GLM 4.7",
  contextSize: 203_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Z.ai's GLM 4.7 model (203k context).",
  shortDescription: "Z.ai's GLM 4.7 model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 4_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GLM47FLASH_MODEL_ID = "glm-4-7-flash" as const;
export const GLM47FLASH_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GLM47FLASH_MODEL_ID,
  modelMaker: "zai",
  displayName: "GLM 4.7 Flash",
  contextSize: 203_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description: "Z.ai's fast, low-cost GLM 4.7 Flash model (203k context).",
  shortDescription: "Z.ai's fast GLM model.",
  isLegacy: false,
  isLatest: false,
  generationTokensCount: 4_000,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};

export const GLM5_MODEL_ID = "glm-5" as const;
export const GLM5_MODEL_CONFIG: StaticModelConfigurationType = {
  providerId: "bedrock",
  modelId: GLM5_MODEL_ID,
  modelMaker: "zai",
  displayName: "GLM 5",
  contextSize: 200_000,
  recommendedTopK: 32,
  recommendedExhaustiveTopK: 64,
  largeModel: true,
  description:
    "Z.ai's GLM 5 flagship model for multi-step reasoning (200k context).",
  shortDescription: "Z.ai's GLM 5 flagship.",
  isLegacy: false,
  isLatest: true,
  generationTokensCount: 16_384,
  supportsVision: false,
  supportsResponseFormat: false,
  supportedReasoningEfforts: {
    none: true,
    light: false,
    medium: false,
    high: false,
  },
  defaultReasoningEffort: "none",
  tokenizer: { type: "tiktoken", base: "o200k_base" },
  regionalAvailability: {
    "us-central1": true,
    "ap-southeast-2": true,
    "europe-west1": false,
  },
};
